import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator, onAuthStateChanged, signInAnonymously, signInWithCustomToken, signOut, GoogleAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator, doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, connectFunctionsEmulator, httpsCallable } from 'firebase/functions';
import { subscribeAccountReadiness } from './authReadiness.js';
import { createPendingTeamCodeStore, prepareGoogleTeamAccount, recoverGoogleTeamRedirect } from './pendingTeamCode.js';

const env = import.meta.env;
const emulator = env.VITE_USE_EMULATORS === 'true';
const fields = { apiKey: 'VITE_FIREBASE_API_KEY', authDomain: 'VITE_FIREBASE_AUTH_DOMAIN', projectId: 'VITE_FIREBASE_PROJECT_ID', appId: 'VITE_FIREBASE_APP_ID' };
const missing = Object.values(fields).filter(name => !env[name]);
const configured = emulator || missing.length === 0;
export const firebaseStatus = Object.freeze({ configured, emulator, missing: emulator ? [] : missing });
const config = Object.fromEntries(Object.entries(fields).map(([key, name]) => [key, env[name] || (key === 'projectId' ? 'demo-charlie-charlie' : key === 'authDomain' ? 'demo-charlie-charlie.firebaseapp.com' : 'demo-key')]));
const hostingDomains = new Set([`${config.projectId}.web.app`, `${config.projectId}.firebaseapp.com`, 'charlie2creato.web.app', 'charlie2creato.firebaseapp.com']);
if (!emulator && hostingDomains.has(location.hostname)) config.authDomain = location.hostname;
if (emulator && !config.projectId.startsWith('demo-')) throw new Error('Emulator mode requires a demo- Firebase project ID to prevent accidental production access.');
export const firebaseApp = configured ? initializeApp(config) : null;
export const auth = firebaseApp ? getAuth(firebaseApp) : null;
export const db = firebaseApp ? getFirestore(firebaseApp) : null;
export const functions = firebaseApp ? getFunctions(firebaseApp, env.VITE_FIREBASE_FUNCTIONS_REGION || 'asia-east2') : null;
if (emulator) {
  const host = env.VITE_EMULATOR_HOST || '127.0.0.1';
  connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  connectFirestoreEmulator(db, host, 8080);
  connectFunctionsEmulator(functions, host, 5001);
}
export function requireFirebase() {
  if (!configured) throw new Error('Firebase is not configured. Local preview does not save a student account or practice history.');
}
export async function callable(name, data, options) {
  requireFirebase();
  const result = await httpsCallable(functions, name, options)(data);
  return result.data;
}
let sessionStorage;
try { sessionStorage=window.sessionStorage; } catch {}
const pendingTeamCode=createPendingTeamCodeStore({storage:sessionStorage});
let pendingTeamLoginError;
const companyAuthorizations = new Map();
async function prepareGoogleAccount(user) {
  if (!companyAuthorizations.has(user.uid)) {
    const company = user.emailVerified && /^[^\s@]+@creatogether\.app$/i.test(user.email || '');
    const operation = prepareGoogleTeamAccount({
      user,currentUser:()=>auth.currentUser,pending:pendingTeamCode,isCompany:company,
      enrol:()=>callable(company ? 'registerCompanyAdmin' : 'registerGoogleStudent', {eventId:crypto.randomUUID()}),
      join:accessCode=>callable('joinTeam',{accessCode,role:'student',eventId:crypto.randomUUID()}),
      refresh:()=>user.getIdToken(true),signOut:()=>signOut(auth),onFailure:error=>{pendingTeamLoginError=error;},
    }).finally(() => companyAuthorizations.delete(user.uid));
    companyAuthorizations.set(user.uid, operation);
  }
  return companyAuthorizations.get(user.uid);
}
export function clearPendingTeamCode() { pendingTeamCode.clear(); }
export function authSubscribe(callback,onLoading=()=>{}) {
  if (!auth) { queueMicrotask(() => {callback(null);onLoading(false);}); return () => {}; }
  return subscribeAccountReadiness({
    onUser:(profile,error)=>{const failure=error||pendingTeamLoginError;pendingTeamLoginError=null;callback(failure?null:profile,failure);},onLoading,resolveRedirect:finishGoogleRedirect,
    watchAuth:(next,error)=>onAuthStateChanged(auth,next,error),currentUser:()=>auth.currentUser,
    enrolGoogle:prepareGoogleAccount,signOut:()=>signOut(auth),
    isAdminPath:()=>location.pathname.replace(/\/$/,'')==='/admin',
    watchProfile:(uid,next,error)=>onSnapshot(doc(db,'users',uid),{includeMetadataChanges:true},snapshot=>next(snapshot.exists()?{uid,...snapshot.data()}:null,snapshot.metadata.fromCache),error),
  });
}
const googleLoginMessage = error => {
  if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request') return 'Google sign-in was cancelled. Please try again.';
  if (error.code === 'auth/popup-blocked') return 'Allow pop-ups for this website, then try again.';
  if (error.code === 'auth/account-exists-with-different-credential') return 'This Google account cannot sign in yet. Contact your workshop organiser.';
  return error.message;
};
export async function signInGoogle(role = 'student', accessCode = '') {
  requireFirebase();
  const flowId=role==='student'?pendingTeamCode.begin(accessCode,'popup'):(pendingTeamCode.clear(),null);
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({...(role === 'admin' ? {hd:'creatogether.app'} : {}),prompt:'select_account'});
  try {
    const result = await signInWithPopup(auth,provider);
    if (flowId&&!pendingTeamCode.bind(result.user.uid,flowId)) throw new Error('Google sign-in was cancelled. Please try again.');
    const profile = await prepareGoogleAccount(result.user);
    if (role === 'admin' && profile.role !== 'admin') { await signOut(auth); throw new Error('Use your @creatogether.app Google account to open Admin.'); }
    if (auth.currentUser?.uid !== result.user.uid) throw new Error('Google sign-in was cancelled. Please try again.');
    return profile;
  } catch (error) { pendingTeamCode.clear(flowId); throw new Error(googleLoginMessage(error)); }
}
export async function signInGoogleRedirect(role = 'student', accessCode = '') {
  requireFirebase();
  const flowId=role==='student'?pendingTeamCode.begin(accessCode,'redirect'):(pendingTeamCode.clear(),null);
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({...(role === 'admin' ? {hd:'creatogether.app'} : {}),prompt:'select_account'});
  try { await signInWithRedirect(auth,provider); }
  catch (error) { pendingTeamCode.clear(flowId); throw new Error(googleLoginMessage(error)); }
}
export async function finishGoogleRedirect() {
  if (!auth) return;
  try {
    await recoverGoogleTeamRedirect({
      pending:pendingTeamCode,resolveRedirect:()=>getRedirectResult(auth),currentUser:()=>auth.currentUser,
      isAdmin:location.pathname.replace(/\/$/,'')==='/admin',
      onFailure:error=>{pendingTeamLoginError=new Error(googleLoginMessage(error));},
      signOut:async user=>{if(auth.currentUser?.uid===user.uid)await signOut(auth);},
    });
  } catch (error) { throw new Error(googleLoginMessage(error)); }
}
export async function signIn({ accessCode, role = 'student' }) {
  requireFirebase();
  if (role === 'student' && !auth.currentUser) throw new Error('Sign in with your Google account to join a team.');
  if (!auth.currentUser) await signInAnonymously(auth);
  const joined = await callable('joinTeam', { accessCode, role, eventId: crypto.randomUUID() });
  if (joined.customToken) await signInWithCustomToken(auth, joined.customToken);
  else await auth.currentUser.getIdToken(true);
  return joined.profile;
}
export async function signOutStudent() { pendingTeamCode.clear(); pendingTeamLoginError=null; if (auth) await signOut(auth); }
